import React, {useState, useEffect, useRef} from 'react';
import Form from 'react-bootstrap/Form';
import './CreatableSelectComponent.css';

const CreatableSelectComponent = ({
  options: initialOptions,
  value,
  onChange,
  placeholder,
  className,
  isInvalid,
  isDisabled = false,
  isLoading = false,
  isClearable = false,
  styles,
}) => {
  const [options, setOptions] = useState([]);
	const [inputValue, setInputValue] = useState('');
	const [showDropdown, setShowDropdown] = useState(false);
	const [filteredOptions, setFilteredOptions] = useState([]);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const wrapperRef = useRef(null);
	const inputRef = useRef(null);

  useEffect(() => {
    // Convert incoming options to { value, label }
    setOptions(
      (initialOptions || []).map(opt => ({
        value: opt.value || opt.label,
        label: opt.label || opt.value,
      }))
    );
  }, [initialOptions]);

	useEffect(() => {
		// Set input value from selected value
		if (value) {
			let displayValue = '';
			if (typeof value === 'object' && value !== null) {
				displayValue = value.label || value.value || '';
			} else {
				displayValue = value;
			}
			setInputValue(String(displayValue));
		} else {
			setInputValue('');
		}
	}, [value]);

	useEffect(() => {
		// Filter options based on input
		const currentInput = String(inputValue || '');
		if (currentInput.trim() === '') {
			setFilteredOptions(options);
		} else {
			const filtered = options.filter(opt => {
				if (!opt) return false;
				const optLabel = typeof opt === 'object' ? (opt.label || opt.value || '') : String(opt);
				return String(optLabel).toLowerCase().includes(currentInput.toLowerCase());
			});
			setFilteredOptions(filtered);
		}
	}, [inputValue, options]);

	useEffect(() => {
		// Close dropdown when clicking outside
		const handleClickOutside = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
				setShowDropdown(false);
				setHighlightedIndex(-1);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleInputChange = (e) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		setShowDropdown(true);
		setHighlightedIndex(-1);
	};

	const handleInputFocus = () => {
		setShowDropdown(true);
	};

	const handleOptionClick = (option) => {
		if (option && typeof option === 'object') {
			setInputValue(String(option.label || option.value || ''));
		} else {
			setInputValue(String(option || ''));
		}
		onChange(option);
		setShowDropdown(false);
		setHighlightedIndex(-1);
	};

	const handleCreateNew = () => {
		const currentInput = String(inputValue || '');
		if (currentInput.trim() === '') {
			return;
		}

		// Check if option already exists
		const existingOption = options.find(opt => {
			if (!opt) return false;
			const optValue = typeof opt === 'object' ? (opt.value || '') : String(opt);
			return String(optValue).toLowerCase() === currentInput.trim().toLowerCase();
		});

		if (existingOption) {
			handleOptionClick(existingOption);
			return;
		}

		const newOption = {value: currentInput.trim(), label: currentInput.trim()};
    setOptions(prev => [...prev, newOption]);
    onChange(newOption);
		setShowDropdown(false);
		setHighlightedIndex(-1);
  };

	const handleKeyDown = (e) => {
		if (!showDropdown) {
			if (e.key === 'ArrowDown' || e.key === 'Enter') {
				setShowDropdown(true);
				return;
			}
		}

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setHighlightedIndex(prev =>
				prev < filteredOptions.length ? prev + 1 : prev
			);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setHighlightedIndex(prev => (prev > -1 ? prev - 1 : -1));
		} else if (e.key === 'Enter') {
			e.preventDefault();
			if (highlightedIndex === -1) {
				// Create new option
				handleCreateNew();
			} else if (highlightedIndex === filteredOptions.length) {
				// Create new option (highlighted on "Create" option)
				handleCreateNew();
			} else if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
				// Select highlighted option
				handleOptionClick(filteredOptions[highlightedIndex]);
			}
		} else if (e.key === 'Escape') {
			setShowDropdown(false);
			setHighlightedIndex(-1);
		}
  };

	const handleClear = () => {
		setInputValue('');
		onChange(null);
		setShowDropdown(false);
		setHighlightedIndex(-1);
		if (inputRef.current) {
			inputRef.current.focus();
		}
	};

	let showCreateOption = false;
	if (inputValue && String(inputValue).trim() !== '') {
		const currentInputLower = String(inputValue).trim().toLowerCase();
		const existsInFiltered = filteredOptions.some(opt => {
			if (!opt) return false;
			const optValue = typeof opt === 'object' ? (opt.value || '') : String(opt);
			return String(optValue).toLowerCase() === currentInputLower;
		});
		showCreateOption = !existsInFiltered;
	}

  return (
	  <div ref={wrapperRef} className="creatable-select-wrapper" style={styles}>
		  <div className="creatable-select-input-container">
			  <Form.Control
				  ref={inputRef}
				  type="text"
				  value={inputValue || ''}
				  onChange={handleInputChange}
				  onFocus={handleInputFocus}
				  onKeyDown={handleKeyDown}
				  placeholder={placeholder}
				  className={`${className || ''} ${isInvalid ? 'is-invalid' : ''}`}
				  disabled={isDisabled}
				  autoComplete="off"
			  />
			  {isClearable && inputValue && !isDisabled && (
				  <button
					  type="button"
					  className="creatable-select-clear-btn"
					  onClick={handleClear}
					  tabIndex={-1}
				  >
					  ×
				  </button>
			  )}
			  {isLoading && (
				  <div className="creatable-select-loading">
					  <div className="spinner-border spinner-border-sm" role="status">
						  <span className="sr-only">Loading...</span>
					  </div>
				  </div>
			  )}
		  </div>

		  {showDropdown && !isDisabled && (
			  <div className="creatable-select-dropdown">
				  {filteredOptions.length === 0 && !showCreateOption ? (
					  <div className="creatable-select-option creatable-select-no-options">
						  {inputValue && String(inputValue).trim()
							  ? `No results found for "${inputValue}"`
							  : "Type to search or create a new option"}
					  </div>
				  ) : (
					  <>
						  {filteredOptions.map((option, index) => {
							  const displayLabel = (option && typeof option === 'object')
								  ? (option.label || option.value || '')
								  : String(option || '');

							  return (
								  <div
									  key={option.value || index}
									  className={`creatable-select-option ${
										  highlightedIndex === index ? 'highlighted' : ''
									  }`}
									  onClick={() => handleOptionClick(option)}
									  onMouseEnter={() => setHighlightedIndex(index)}
								  >
									  {displayLabel}
								  </div>
							  );
						  })}
						  {showCreateOption && (
							  <div
								  className={`creatable-select-option creatable-select-create-option ${
									  highlightedIndex === filteredOptions.length ? 'highlighted' : ''
								  }`}
								  onClick={handleCreateNew}
								  onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
							  >
								  <strong>Create "{inputValue}"</strong>
							  </div>
						  )}
					  </>
				  )}
			  </div>
		  )}
	  </div>
  );
};

export default CreatableSelectComponent;
